<svelte:options customElement={{
          tag: 'chat-bot',
          shadow: 'open'
        }} />
<script lang="ts">
  import "@/app.css";
  type UrlConfig = {
    baseURL?: string;
    protocol?: string;
    channelPath: string;
  };
  type Props = {
    windowMode: string | boolean;
    notificationMessage: string;
    inputPlaceholderText: string;
    searchSubMessage: string;
    cssUrl: string;
    urlConfiguration: UrlConfig;
    isBrowser: boolean;
    logoSrc?: string;
  };

  let {
    windowMode = false,
    notificationMessage = "",
    inputPlaceholderText = "",
    searchSubMessage = "",
    cssUrl = "",
    urlConfiguration = $bindable({
      baseURL: undefined,
      protocol: undefined,
      channelPath: "/ws/chatbot",
    }),
    isBrowser = true,
    logoSrc = ""
  }: Props = $props();
  let initDone = $state(false);
  import { onMount, onDestroy } from "svelte";
  import type {
    ChatEntry,
    ChatStore,
  } from "./chatbotClientStore";

  const storeVersion = 1.22;
  let lsSyncing = $state(false);
  let lsSyncingDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  let receivingMessages = $state(false);
  let receivingMessagesTimeout: ReturnType<typeof setTimeout> | null = null;
  function newChatbotStore(chatId: string | undefined = undefined, notificationDismissed: boolean = false): ChatStore {
      return {
          storeVersion: storeVersion,
          chatId: chatId,
          notificationDismissed: notificationDismissed,
          lastActivity: Date.now(),
          isTyping: {
            user: false,
            assistant: false,
          },
          chatHistory: [],
          shouldClearOnConnect: false
        }
    }
  let chatBotStore = $state<ChatStore>(newChatbotStore());

  import { sleep } from "./utils";
  import Loading from "./Loading.svelte";
  import ChatMessage from "./ChatMessage.svelte";
  import IconSendMessage from "./IconSend.svelte";
  import MinimizeIcon from "./MinimizeIcon.svelte";
  import UnmaximizeIcon from "./IconUnmaximize.svelte";
  import ChatIcon from "./IconChatMore.svelte";
  import IconMaximize from "./IconMaximize.svelte";
  import IconCloseSlim from "./IconCloseSlim.svelte";
  let chatInput = $state("");
  let enableSend = $state(false);
  let messageTextArea = $state() as HTMLTextAreaElement ;
  let messageForm = $state() as HTMLFormElement;
  let notificationBoxDiv = $state() as HTMLDivElement;

  type BotConnection = {
    webSocketEstablished: boolean;
    ws: WebSocket | null;
    chatIsOngoing: boolean;
    retry: number;
    isErrorState: boolean;
    errorMessage: string;
  };
  const botConnection: BotConnection = $state({
    webSocketEstablished: false,
    ws: null,
    chatIsOngoing: false,
    retry: 5,
    isErrorState: false,
    errorMessage: ""
  });
  const commands = ["/clear"];
  const shell = $state({
    isExpanded: (windowMode === "true" || windowMode === true) ? true : false,
    isTransitioning: false,
    isMaximized: false
  });

  function saveLocalStorage(store: ChatStore) {
    if(windowMode) return;
    try {
      if(!store) return;
      if(chatBotStore?.chatId) {
        localStorage.setItem("trvChatbotStore", JSON.stringify(store));
      }
    } catch(e) {
      console.error("Failed to save local storage", e);
    }
  }

  function loadLocalStorage() {
    if(windowMode) return;
    try {
      const storeJSON = localStorage.getItem("trvChatbotStore");
      if (storeJSON) {
        const lsStore = JSON.parse(storeJSON);
        if(lsStore?.storeVersion !== storeVersion) {
          console.warn("Store version mismatch, resetting store");
          chatBotStore = newChatbotStore();
        } else {
          if(lsStore?.chatId) {
            // Check if last activity was more than 4 hours ago
            const fourHoursAgo = Date.now() - (4 * 60 * 60 * 1000);
            if(lsStore.lastActivity && lsStore.lastActivity < fourHoursAgo) {
              console.log("Chat session expired (>4 hours), will clear on next connection");
              // Set a flag to clear chat on next WebSocket connection
              chatBotStore = newChatbotStore();
              chatBotStore.shouldClearOnConnect = true;
            } else {
              chatBotStore = lsStore;
            }
          }
        }
      }
    } catch(e) {
      console.error("Failed to load local storage", e);
    }
  }

  function getId() {
    return (
      chatBotStore.chatHistory.filter((entry) => entry.event === "chat")
        .length + 1
    );
  }

  async function waitForConnection() {
    let waitId: any = undefined;
    let iterCount = 0;
    const result = await new Promise((resolve) => {
      waitId = setInterval(() => {
        if (iterCount > 250)
        resolve(botConnection.webSocketEstablished);
        if (botConnection.webSocketEstablished) {
          clearInterval(waitId);
          resolve(botConnection.webSocketEstablished);
        }
        iterCount++;
      }, 20);
    });
    const status = result
      ? "Chatbot connection ready"
      : "Chatbot failed to connect to websocket";
    if (result === false) {
      chatBotStore.chatHistory.push({
        id: getId() + 100, //? error not counted, add 100 to prevent id collison
        event: "error",
        name: "Järjestelmä",
        content:
        "Yhteyden muodostaminen palvelimeen epäonnistui, koita myöhemmin uudestaan.",
        role: "system",
        timestamp: Date.now()
      });
      console.log(status);
    }
    return result;
  }

  async function waitForChatId() {
    let waitId: any = undefined;
    let iterCount = 0;
    if (chatBotStore?.chatId) return;
    const result = await new Promise((resolve) => {
      waitId = setInterval(() => {
        if (iterCount > 250) {
          clearInterval(waitId);
          resolve(false);
        }
        if (chatBotStore?.chatId) {
          clearInterval(waitId);
          resolve(true);
        }
        iterCount++;
      }, 20);
    });
    return result;
  }

  async function command(command: string) {
    console.log(command);
    if (command === "/clear") {
      const chatEntry: ChatEntry = {
        event: "clear",
        id: 10000,
        role: "user",
        content: "",
        name: "user",
        timestamp: Date.now()
      };
      botConnection.ws?.send(
        JSON.stringify({
          chatId: chatBotStore.chatId,
          chatEntry,
        })
      );
    }
  }

  async function onSubmit(e: SubmitEvent | undefined = undefined) {
    if(e) e.preventDefault();
    if(!enableSend) return;
    chatBotStore.lastActivity = Date.now();
    if (commands.includes(chatInput.trim())) {
      await command(chatInput.trim());
      chatInput = "";
      onMessageInput();
      return;
    }

    if (!botConnection.webSocketEstablished) {
      botConnection.chatIsOngoing = true;
      establishWebSocket();
      await waitForConnection();
    }

    await waitForChatId();
    chatBotStore.isTyping.assistant = true;
    const entryId = getId();

    const chatEntry: ChatEntry = {
      id: entryId,
      event: "chat",
      name: "Asiakas",
      role: "user",
      content: chatInput,
      done: true,
      partials: [{ id: 0, content: chatInput }],
      timestamp: Date.now()
    };
    chatBotStore.chatHistory.push(chatEntry);

    const assistantEntry: ChatEntry = {
      id: entryId+1,
      event: "chat",
      name: "Assistant",
      role: "assistant",
      content: "",
      done: false,
      partials: [],
      timestamp: Date.now()
    };
    chatBotStore.chatHistory.push(assistantEntry);

    botConnection.ws?.send(
      JSON.stringify({
        chatId: chatBotStore.chatId,
        chatEntry: chatEntry,
      })
    );

    chatInput = "";
    onMessageInput();
  }

  async function onFocus() {
    botConnection.chatIsOngoing = true;
    establishWebSocket();
    await waitForConnection();
  }

  async function onMessage(event: MessageEvent<any>) {
    if (!event?.data) return;
    try {
      const message = JSON.parse(event.data);
      if(lsSyncing) {
        console.warn("Message event while syncing");
        return;
      }

      // Mark that we are receiving messages
      receivingMessages = true;

      // Set a timeout to reset receivingMessages in case the message doesn't properly signal completion
      if (receivingMessagesTimeout) {
        clearTimeout(receivingMessagesTimeout);
      }
      receivingMessagesTimeout = setTimeout(() => {
        receivingMessages = false;
      }, 5000); // Reset after 5 seconds if not properly reset

      if(message?.event === "chat_partial") {
        chatBotStore.lastActivity = Date.now();
        if(!Number.isNaN(message?.id)) {
          const messageIdx = chatBotStore.chatHistory.findIndex((entry) => entry.id === message.id);
          if(messageIdx !== -1) {
            const partialIdx = chatBotStore.chatHistory[messageIdx].partials?.findIndex((part) => part.id === message.partialId);
            if(message?.done) {
              chatBotStore.chatHistory[messageIdx].done = true;
              console.log("message done");
              // Reset receivingMessages when server says message is complete
              receivingMessages = false;
              chatBotStore.isTyping.assistant = false;
              // Clear the timeout since we properly received completion
              if (receivingMessagesTimeout) {
                clearTimeout(receivingMessagesTimeout);
                receivingMessagesTimeout = null;
              }
              // chatBotStore.chatHistory = chatBotStore.chatHistory;
            } else if(chatBotStore.chatHistory[messageIdx]?.done !== false) {
              chatBotStore.chatHistory[messageIdx].done = false;
            }
            if(partialIdx === -1) {
              chatBotStore.chatHistory[messageIdx].partials?.push({
                id: message.partialId,
                content: message.content,
              });
              chatBotStore.chatHistory[messageIdx].timestamp = Date.now();
              if(chatBotStore.chatHistory[messageIdx]?.partials) {
                chatBotStore.chatHistory[messageIdx].partials.sort((a, b) => (a.id > b.id ? 1 : -1))
              }
            }
          }
        } else {
          console.error("Chat partial with no idx", message);
        }
      }

      if (message?.event && message.event === "set_chat_id") {
        chatBotStore = newChatbotStore(message.content, chatBotStore.notificationDismissed);
        chatBotStore.lastActivity = Date.now();
        if (message?.data?.chatEntry) {
          message.data.chatEntry.partials = [{
            id: 0,
            content: message.data.chatEntry.content,
          }];
          message.data.chatEntry.timestamp = Date.now();
          message.data.chatEntry.done = true;
          chatBotStore.chatHistory = [message.data.chatEntry];
        }
        receivingMessages = false;
      }
    } catch (e) {
      console.error("onMessage error", e);
      console.error("DATA", event?.data);
    }
  }

  // //** Create websocket connection and event listeners
  async function establishWebSocket() {
    try {
      if(botConnection.webSocketEstablished) return;
      if (isBrowser === false || !window?.location) {
        botConnection.webSocketEstablished = false;
        return;
      }

      if (isBrowser && !urlConfiguration?.baseURL) {

        urlConfiguration = {
          baseURL: window.location.host,
          protocol:
          window.location.protocol === "https:" ? "wss" : "ws",
          channelPath: "/ws/chatbot",
        };
      }

      botConnection.ws = new WebSocket(`${urlConfiguration.protocol}://${urlConfiguration.baseURL}${urlConfiguration.channelPath}`);

      botConnection.ws.addEventListener("open", async () => {
        botConnection.webSocketEstablished = true;
        botConnection.chatIsOngoing = true;
        botConnection.isErrorState = false;

        if (!chatBotStore?.chatId || chatBotStore?.storeVersion !== storeVersion || chatBotStore?.shouldClearOnConnect) {
          console.log("Conn open", chatBotStore?.chatId, chatBotStore?.storeVersion, "shouldClear:", chatBotStore?.shouldClearOnConnect)
          console.log("Websocket connected", $state.snapshot(botConnection));
          await command("/clear");
          await waitForChatId();
          // Reset the flag after clearing
          if (chatBotStore?.shouldClearOnConnect) {
            chatBotStore.shouldClearOnConnect = false;
          }
        }
      });
      botConnection.ws.addEventListener("close", () => {
        botConnection.webSocketEstablished = false;
        if (botConnection.chatIsOngoing && botConnection?.retry > 0) {
          botConnection.retry = botConnection.retry - 1;
          establishWebSocket();
        }
      });

      botConnection.ws.addEventListener("error", (event) => {
        console.error("WebSocket error:", event);
        if(botConnection?.ws?.readyState === 3) {
          if (botConnection.chatIsOngoing && botConnection?.retry > 0) {
            botConnection.retry = botConnection.retry - 1;
            botConnection.webSocketEstablished = false;
            establishWebSocket();
          }
        }
      });

      botConnection.ws.addEventListener("message", (event) =>
        onMessage(event)
      );
    } catch (e) {
      console.log("Error with websocket", e, urlConfiguration);
    }
  }


  function onMessageInput() {
    messageTextArea.style.height = "max-content";
    if(chatInput.trim().length > 0) {
      enableSend = true;
      messageTextArea.style.height = messageTextArea.scrollHeight + "px";
    } else {
      enableSend = false;
    }
  }

  function onKeyDown(e: KeyboardEvent) {
    if(e.key.toLowerCase() === "enter" && e.shiftKey === false) {
      e.preventDefault();
      if(chatInput.trim().length > 0) {
        onSubmit();
        onMessageInput();
      }
    }
  }

  async function dismissNotification() {
    if(chatBotStore?.notificationDismissed) return;
    notificationBoxDiv.style.opacity = "0";
    await sleep(300);
    if(chatBotStore?.notificationDismissed !== true) chatBotStore.notificationDismissed = true;
  }

  let componentElement = $state();

  $effect(() => {
    //Inject CSS
    if (cssUrl && componentElement) {
      // Get the host element and its shadow root
      //@ts-ignore
      const host = componentElement.getRootNode()?.host;
      const linkId = `css-${cssUrl.replace(/[^a-zA-Z0-9]/g, '-')}`;
      if(host && !host.shadowRoot.querySelector(`#${linkId}`)) {
        const link = document.createElement('link');
        link.id = linkId;
        link.rel = 'stylesheet';
        link.href = cssUrl;
        link.onerror = () => console.error('Failed to load CSS:', cssUrl);
        host.shadowRoot.insertBefore(link, host.shadowRoot.firstChild);
      }

    }
  });


  $effect(() => {
    if(initDone && !lsSyncing) {
      saveLocalStorage(chatBotStore);
    }
  });

  onMount(() => {
    if(!initDone) {
      windowMode = (windowMode === "true") ? true : false;
      if(typeof urlConfiguration === 'string') {
        urlConfiguration = JSON.parse(urlConfiguration);
      }
      loadLocalStorage();

      window.addEventListener("storage", (event) => {
        if(windowMode) return;
        // Only sync if the event is from another window and for our key
        // and we're not currently receiving messages from the server
        if (event.key === "trvChatbotStore" && event.newValue !== null && !receivingMessages) {
          lsSyncing = true;
          loadLocalStorage();

          // Clear existing timer if it exists
          if (lsSyncingDebounceTimer) {
            clearTimeout(lsSyncingDebounceTimer);
          }

          // Set debounced timer to reset lsSyncing after 300ms
          lsSyncingDebounceTimer = setTimeout(() => {
            lsSyncing = false;
            lsSyncingDebounceTimer = null;
          }, 300);
        }
      });

      initDone = true;
    }
  });

  onDestroy(() => {
    if(isBrowser) {
      botConnection.webSocketEstablished = false;
      botConnection.chatIsOngoing = false;
      botConnection.ws?.close();
      botConnection.ws = null;
    }

    // Clear any remaining timeouts
    if (receivingMessagesTimeout) {
      clearTimeout(receivingMessagesTimeout);
      receivingMessagesTimeout = null;
    }
    if (lsSyncingDebounceTimer) {
      clearTimeout(lsSyncingDebounceTimer);
      lsSyncingDebounceTimer = null;
    }
  });

  </script>

<div bind:this={componentElement} class=" h-full w-full chatbot-container"
  class:chatbot-fullpage-container={windowMode} style={windowMode ? "z-index: 0;" : "z-index: 888"}>

    <section
      class:chatbot-maximized={!windowMode && shell.isMaximized  && shell.isExpanded}
      class="chatbot flex bottom-0 right-0 flex-col border border-w2w-main
      transition-all duration-300
      {windowMode ? "chatbot-fullpage":"absolute"}
      chatbot-fullpage-light

      {shell.isExpanded
      ? `${windowMode ? "chatbot-expanded-fullpage" : "rounded-md chatbot-expanded"}`
      : 'rounded-full w-24 h-24'}
      "
      ontransitionend={() => {
        if (shell.isTransitioning !== false) shell.isTransitioning = false;
      }}
      ontransitionstart={() => {
        if (shell.isTransitioning !== true) shell.isTransitioning = true;
      }}>
        <!-- ** NOTIFICATION BOX *** -->
        {#if !windowMode && !chatBotStore?.notificationDismissed}
          <div class="relative h-0 transition-opacity duration-500" style="opacity: 1;" bind:this={notificationBoxDiv}>
            <div class="chatbot-notification-box">
              <button
                onclick={dismissNotification}
                class="absolute top-0 right-0 p-2 hover:opacity-50">
                  <IconCloseSlim />
                </button>
                <div>{@html notificationMessage}</div>
            </div>
          </div>
          {/if}
        <!-- ** LOADING OVERLAY ** -->
        {#if shell.isExpanded && !shell.isTransitioning && !botConnection?.webSocketEstablished && windowMode === false}
          <div
            class="chatbot_loading_container"
            class:chatbot_loading_container-fullpage={windowMode}
          >
            <Loading width={60} />
          </div>
          {/if}
        {#if botConnection?.isErrorState && shell.isExpanded}
          <div
            class="chatbot_loading_container">
              {botConnection?.errorMessage}
            </div>
            {/if}
        {#if !windowMode}
          <header
            class="chatbot_header flex items-center justify-center {!shell.isExpanded ||
            shell.isTransitioning
            ? 'w-full h-full absolute bg-blue-600'
            : 'chatbot_header_expanded'}
            {!shell.isExpanded && !windowMode ? "rounded-full" : ""}
            ">

              {#if !shell.isExpanded || shell.isTransitioning}
                <!-- ** Expand button -->
                <button
                  class="w-full h-full flex items-center justify-center rounded-full bg-blue-600 pl-[3px] pb-[3px]"
                  onclick={() => {
                    shell.isExpanded = true;
                    dismissNotification();
                    establishWebSocket();
                  }}>
                      <!-- <div>
                          <img src={`${logoSrc}&width=50&height=50`} alt="TerveysVoima" class="w-12 h-12" />
                      </div> -->
                      <ChatIcon />
                  </button>
                  {:else}
                  <div
                    class="chatbot_header_logo_container shrink flex items-center justify-center bg-background-1 rounded-full m-4 mr-6">
                        <!-- <W2wLogo /> -->
                        {#if logoSrc}
                            <img src={`${logoSrc}&width=50&height=50`} alt="TerveysVoima" class="w-12 h-12" />
                            {/if}
                    </div>
                    <div class="grow flex flex-col">
                      <div class="text-black font-semibold">Asiakaspalvelu</div>
                      <div class="chabot_header_subtitle text-md grow text-stone-400">AI avustaja</div>
                    </div>
                    <div class="h-full mt-1">
                      <button class="w-12 mt-1 inline-flex justify-end items-end text-black hover:text-gray-700" onclick={() => shell.isMaximized = !shell.isMaximized}>
                        {#if shell.isMaximized}
                          <UnmaximizeIcon style="width: 1.6rem; height: 1.6rem;" />
                          {:else}
                          <IconMaximize style="width: 1.6rem; height: 1.6rem;" />
                          {/if}
                      </button>
                      <button
                        class="w-12 mt-1 text-black hover:text-gray-700"
                        onclick={() => (shell.isExpanded = false)}>
                          <MinimizeIcon />
                        </button>
                    </div>
                    {/if}
            </header>
            {/if} <!-- window mode -->
        <!-- ** CHAT MESSAGES -->
        <div
          class="chatbot_messages flex flex-col grow p-4
          {windowMode ? "chatbot-messages-fullpage chatbot_messages-fullpage-light" : ""}

          {!shell.isExpanded || shell.isTransitioning ? 'hidden' : ''}"
          class:scrollbar-style={!windowMode}
        >
          {#each chatBotStore.chatHistory as chatEntry, i}
            {#if chatEntry.event === "chat" || chatEntry.event === "error"}
              <ChatMessage bind:chatEntry={chatBotStore.chatHistory[i]} {windowMode} />
              {/if}
            {/each}
        </div>

        <!-- ** INPUT BAR ** -->
        <div
          class="relative flex flex-col items-center justify-center gap-2 {!shell.isExpanded || shell.isTransitioning
          ? 'hidden'
          : 'chatbot_send_msg_input_container'}
          {windowMode && "chatbot_send_msg_input_container-fullpage-light"}
          "
          class:chatbot_send_msg_input_container-fullpage={windowMode}>
            <form bind:this={messageForm} onsubmit={onSubmit}
              class="chatbot_send_msg_form flex items-center w-full
              {windowMode && "chatbot_send_msg_form-fullpage-light"}
              "
              class:chatbot_send_msg_form-fullpage={windowMode}>
                <textarea
                  bind:this={messageTextArea}
                  rows="1"
                  class="chatbot_send_msg_input resize-none"
                  class:chatbot_send_msg_input-fullpage={windowMode}
                  placeholder={inputPlaceholderText}
                  bind:value={chatInput}
                  onfocus={onFocus}
                  oninput={onMessageInput}
                  onkeydown={onKeyDown}
                  disabled={lsSyncing || receivingMessages}
                ></textarea>
                <button type="submit" class="chatbot_sendmsg_btn" class:chatbot_sendmsg_btn-fullpage={windowMode}
                  class:chatbot_sendmsg_btn-fullpage-active={windowMode && chatInput.trim().length > 0}
                  disabled={!enableSend}>
                    <IconSendMessage />
                  </button>
              </form>
              {#if windowMode}
                <div class="text-[1rem] pb-6 ">{searchSubMessage}</div>
                {/if}
          </div>
      </section>
      <!-- ? #chatbot -->
  </div>

  <!-- ? #chatbot-container -->
  <style>
    /* === BASE STYLES === */
    .chatbot-container {
      @import url('https://fonts.googleapis.com/css2?family=Martel+Sans:wght@200;300;400;600;700;800;900&display=swap');
      font-family: 'Martel Sans', sans-serif;
      line-height: 1.2;
      position: fixed;
      bottom: 2rem;
      right: 2rem;
      height: 0;
      width: 100%;

      /* CSS Variables */
      --shadow: 0 0px 3px 2px rgba(0, 0, 0, 0.1);
      --color-w2w-main: 68, 166, 73;
      --color-w2w-gray: 52, 58, 64;
      --color-w2w-gray-2: 108, 117, 125;
      --color-w2w-gray-3: 137, 137, 137;
      --color-w2w-gray-4: 236, 236, 236;
      --color-base-background-1: 255, 255, 255;
      --color-text: 16, 16, 16;
      --gd-light: 68, 70, 84;
      --gd-dark: 52, 53, 65;

      /* Global text styles */
      :global(a[href]) {
        text-decoration: underline;
      }
      :global(ul:not(:last-child), ol:not(:last-child), p:not(:last-child), table:not(:last-child)) {
        margin-bottom: 0.8em;
      }
      :global(ul, ol) {
        margin-left: 1.1em;
      }
      :global(ul) {
        list-style: initial;
      }
      :global(ol) {
        list-style: decimal;
      }
    }

    .chatbot-fullpage-container {
      position: relative;
      height: max-content;
      left: 0;
      line-height: 1.7;
    }

    /* === CHATBOT MAIN STYLES === */
    .chatbot {
      border-width: 1.5px;
      background-color: rgb(var(--color-base-background-1));
      border: 1.5px solid hsl(0 0% 64% / 0.5);
      box-shadow: var(--shadow);
    }

    .chatbot-fullpage {
      position: relative;
      min-height: 100vh;
    }

    .chatbot-fullpage-light {
      background-color: rgba(255,255,255,1);
      box-shadow: none;
    }

    .chatbot-expanded-fullpage {
      width: 100%;
      height: 100%;
      border-width: 0;
    }

    .chatbot-expanded {
      width: clamp(10rem, 45rem, calc(100% - 3rem));
      height: clamp(20rem, 55rem, calc(100vh - 4rem));
      border-radius: 1rem;
    }

    .chatbot-maximized {
      width: calc(100% - 4rem);
      height: calc(100vh - 4rem);
    }

    /* === HEADER STYLES === */
    .chatbot_header {
      color: rgb(var(--color-base-background-1));
      z-index: 51;
    }

    .chatbot_header_logo_container {
      width: 4.8rem;
      height: 4.8rem;
    }

    :global(.chatbot_header_logo_container > svg) {
      width: 100%;
      height: 100%;
    }

    .chatbot_header_expanded {
      height: 8rem;
    }

    /* === MESSAGES STYLES === */
    .chatbot_messages {
      background-color: rgba(var(--color-w2w-gray-4), 0.7);
      color: rgb(var(--color-text));
      gap: 1.5rem;
      overflow-y: auto;
      overscroll-behavior-y: contain;
    }

    .chatbot-messages-fullpage {
      gap: 0;
      overflow-y: unset;
      padding: 0;
    }

    .chatbot_messages-fullpage-light {
      background-color: rgba(var(--color-base-background-1), 1);
    }

    .chatbot_messages-fullpage-dark {
      background-color: rgba(var(--gd-light), 1);
      color: rgba(255,255,255,0.8);
    }

    /* === INPUT FORM STYLES === */
    .chatbot_send_msg_input_container {
      padding: 1.5rem 1rem;
    }

    .chatbot_send_msg_input_container-fullpage {
      position: sticky;
      bottom: 0;
      height: min-content;
      padding-left: 2rem;
      padding-right: 2rem;
    }

    .chatbot_send_msg_input_container-fullpage-light {
      background-color: rgba(255,255,255,1);
    }

    .chatbot_send_msg_form {
      height: 100%;
      gap: 1rem;
    }

    .chatbot_send_msg_form-fullpage {
      width: clamp(10rem, 95.5rem, 100%);
      box-shadow: 0 0 2rem 0.5rem rgb(229, 229, 229);
      border: 1.5px solid rgb(229, 229, 229);
      padding: 1.5rem;
      border-radius: 1rem;
      margin-top: -2.5rem;
    }

    .chatbot_send_msg_form-fullpage-light {
      box-shadow: 0 0 transparent, 0 0 transparent, 0 0 15px 0px rgba(0, 0, 0, 0.1);
      background-color: rgba(255,255,255,1);
    }

    .chatbot_send_msg_input {
      display: block;
      flex-grow: 1;
      width: 100%;
      padding: 1rem 1.2rem;
      border: 1px solid var(--color-gray-300);
      border-radius: 0.65rem;

      &:focus-visible {
        border: 2px solid var(--color-gray-300);
        outline: 2px solid var(--color-gray-300);
        outline-offset: 1px;
      }
    }

    .chatbot_send_msg_input-fullpage {
      height: min-content;
      max-height: 20rem;

      &:focus {
        border-width: 0;
      }
    }

    .chatbot_sendmsg_btn {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 0.3rem;
      width: 5rem;
      height: 4rem;
      border-radius: 1rem;
      background-color: var(--color-emerald-600);
      color: #fff;

      &:hover {
        opacity: 0.8;
      }

      &:disabled {
        opacity: 0.5;
      }
    }

    .chatbot_sendmsg_btn-fullpage {
        width: 5.5rem;
      height: 4.8rem;
      border-radius: 0.5rem;
      margin-right: 0.5rem;
    }

    .chatbot_sendmsg_btn-fullpage-active {
      background-color: rgba(25, 195, 125, 1);
      color: #fff !important;
    }

    /* === LOADING OVERLAY === */
    :global(.chatbot_loading_container) {
      background-color: rgba(0,0,0, 0.5);
      color: rgba(var(--color-base-background-1), 1);
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      width: 100%;
      height: 100%;
      bottom: 0;
      left: 0;
      z-index: 50;
    }

    :global(.chatbot_loading_container-fullpage) {
      height: 100%;
    }

    :global(.chatbot_loading_container > div > svg) {
      width: 4rem;
    }

    /* === NOTIFICATION BOX === */
    .chatbot-notification-box {
      position: absolute;
      bottom: 0;
      right: 2rem;
      min-width: 20rem;
      border: 1px solid rgba(var(--color-w2w-gray-3), 0.4);
      border-radius: 0.5rem;
      margin-bottom: 1rem;
      margin-right: -1rem;
      padding: 1rem;
      background: rgba(var(--color-base-background-1), 1);
    }

    /* === SCROLLBAR STYLES === */
    .scrollbar-style {
      scrollbar-width: auto;
      scrollbar-color: var(--color-blue-600) rgb(var(--color-w2w-gray-4));
    }

    .scrollbar-style::-webkit-scrollbar {
      width: 15px;
    }

    .scrollbar-style::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 5px;
    }

    .scrollbar-style::-webkit-scrollbar-thumb {
      background-color: rgb(var(--color-w2w-main));
      border-radius: 14px;
      border: 3px solid rgb(var(--color-w2w-main));
    }

    /* === RESPONSIVE STYLES === */
    @media screen and (max-width: 1160px) {
      .chatbot_send_msg_input_container-fullpage {
        padding-right: 5rem;
      }
    }

    @media screen and (max-width: 1100px) {
      .chatbot_send_msg_input_container-fullpage {
        padding-right: 7rem;
      }
    }

    @media screen and (max-width: 1080px) {
      .chatbot_send_msg_input_container-fullpage {
        padding-right: 9rem;
      }
    }
    </style>
